
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HeroProps {
  cartCount: number;
  onCartClick: () => void;
}

export default function Hero({ cartCount, onCartClick }: HeroProps) {
  const { t, i18n } = useTranslation('common');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
  };

  return (
    <header className="relative text-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 sm:h-32 md:h-36">
            <div className="flex items-center flex-shrink-0">
              <a href="/" onClick={(e) => { e.preventDefault(); scrollToTop(); }} className="cursor-pointer">
                <img 
                  src="https://static.readdy.ai/image/a808dc7181425b7ee1ae1c223286d53e/5fb889009dc71ddc5dfbd3521970a934.png" 
                  alt="Eat Fresh by 2π - Logo" 
                  className="h-20 sm:h-28 md:h-32 w-auto object-contain"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); scrollToTop(); }}
                className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer text-gray-700"
              >
                {t('nav.home')}
              </a>
              <a
                href="/#fresh"
                onClick={(e) => { e.preventDefault(); scrollToSection('fresh'); }}
                className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer text-gray-700"
              >
                {t('nav.freshFish')}
              </a>
              <a
                href="/#grilled"
                onClick={(e) => { e.preventDefault(); scrollToSection('grilled'); }}
                className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer text-gray-700"
              >
                {t('nav.grilledFish')}
              </a>
              <a
                href="/#about"
                onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}
                className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer text-gray-700"
              >
                {t('nav.about')}
              </a>
              <a
                href="/#contact"
                onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
                className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer text-gray-700"
              >
                {t('nav.contact')}
              </a>
              <a
                href="/fournisseur"
                className="px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer text-gray-700"
              >
                {t('nav.supplier')}
              </a>
              
              {/* Language Switcher */}
              <button
                onClick={toggleLanguage}
                className="ml-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer text-gray-700 flex items-center gap-1"
              >
                <i className="ri-global-line"></i>
                {i18n.language === 'fr' ? 'EN' : 'FR'}
              </button>
            </div>

            {/* Mobile menu button and Cart */}
            <div className="flex items-center gap-2">
              {/* Language Switcher Mobile */}
              <button
                onClick={toggleLanguage}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <span className="text-sm font-bold text-gray-700">
                  {i18n.language === 'fr' ? 'EN' : 'FR'}
                </span>
              </button>
              
              <button 
                onClick={onCartClick}
                className="relative bg-teal-600 text-white px-3 sm:px-4 py-2 rounded-full hover:bg-teal-700 transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-shopping-cart-line text-base sm:text-lg"></i>
                <span className="font-medium hidden sm:inline">{t('nav.cart')}</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl text-gray-700`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 space-y-2 border-t border-gray-200 bg-white">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); scrollToTop(); setIsMenuOpen(false); }}
                className="block px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-700"
              >
                {t('nav.home')}
              </a>
              <a
                href="/#fresh"
                onClick={(e) => { e.preventDefault(); scrollToSection('fresh'); setIsMenuOpen(false); }}
                className="block px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-700"
              >
                {t('nav.freshFish')}
              </a>
              <a
                href="/#grilled"
                onClick={(e) => { e.preventDefault(); scrollToSection('grilled'); setIsMenuOpen(false); }}
                className="block px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-700"
              >
                {t('nav.grilledFish')}
              </a>
              <a
                href="/#about"
                onClick={(e) => { e.preventDefault(); scrollToSection('about'); setIsMenuOpen(false); }}
                className="block px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-700"
              >
                {t('nav.about')}
              </a>
              <a
                href="/#contact"
                onClick={(e) => { e.preventDefault(); scrollToSection('contact'); setIsMenuOpen(false); }}
                className="block px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-700"
              >
                {t('nav.contact')}
              </a>
              <a
                href="/fournisseur"
                className="block px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-700"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('nav.supplier')}
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Content with Background Image */}
      <div className="relative pt-24 sm:pt-32 md:pt-36" style={{ minHeight: '100vh' }}>
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(https://static.readdy.ai/image/a808dc7181425b7ee1ae1c223286d53e/cb6f13d59fa3b122e1a656ba74b7f99b.jpeg)'
          }}
        >
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/80 via-teal-800/75 to-teal-700/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center" style={{ minHeight: 'calc(100vh - 144px)' }}>
          <div className="w-full py-12 sm:py-16 md:py-20">
            <div className="max-w-2xl">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight drop-shadow-lg">
                {t('hero.title')}
              </h2>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/95 mb-6 sm:mb-8 leading-relaxed drop-shadow-md">
                {t('hero.description')}
                <br />
                <br />
                {t('hero.delivery')}
                <br />
                <br />
                {t('hero.principle')}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <a href="/#fresh" className="bg-teal-600 text-white px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer shadow-lg">
                  {t('hero.orderFresh')}
                </a>
                <a href="/#grilled" className="bg-white text-teal-600 px-5 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-full text-sm sm:text-base md:text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer shadow-lg">
                  {t('hero.viewGrilled')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
